import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateEvent } from '../api/owner'
import type { EventVisibility } from '../api/ownerTypes'
import { useEvent } from './eventContext'
import { EventScreen, SaveBar } from './components/EventScreen'
import { EventPeople } from './components/EventPeople'
import {
  AccentChoice,
  PrivateGalleryChoice,
  VisibilityChoice,
} from './components/EventSettingsFields'

/**
 * Ronde 20I en 20U — privacy en kleur.
 *
 * Hier stond ook "feest afsluiten", met een eigen knop en een bevestiging. Dat
 * is weg. Afsluiten deed twee dingen tegelijk — uploads stoppen, maar kijken
 * toestaan — en niemand kon onthouden wat er nu precies dicht ging. Er staat nu
 * één keuze voor in de plaats, bij de andere instellingen: de galerij op privé.
 * Dan is de gastenkant voorbij, en de eigenaar kan er altijd nog in.
 *
 * Het is een gewoon veld geworden en geen besluit met een dialoog erbij, want
 * dat is het ook: je zet hem aan en je zet hem weer uit, net zo vaak als je wil.
 */
export function EventSettingsPage() {
  const { subject, refresh } = useEvent()
  const queryClient = useQueryClient()

  const [visibility, setVisibility] = useState<EventVisibility>(subject.visibility)
  const [accent, setAccent] = useState(subject.accentColor)
  const [isPrivate, setIsPrivate] = useState(subject.isPrivate)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dirty =
    visibility !== subject.visibility ||
    accent !== subject.accentColor ||
    isPrivate !== subject.isPrivate

  async function save() {
    setSaving(true)
    setError(null)

    try {
      await updateEvent(subject.id, { visibility, accentColor: accent, isPrivate })
      await refresh()
      await queryClient.invalidateQueries({ queryKey: ['owner', 'events'] })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Opslaan lukte niet.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <EventScreen
        title="Instellingen"
        eventTitle={subject.title}
        desktopSubtitle="Beide keuzes kunnen tijdens het feest nog wijzigen."
        footer={
          <>
            {error && (
              <p role="alert" className="text-accent mb-3 text-sm">
                {error}
              </p>
            )}
            <SaveBar
              saving={saving}
              dirty={dirty}
              onCancel={() => {
                setVisibility(subject.visibility)
                setAccent(subject.accentColor)
                setIsPrivate(subject.isPrivate)
              }}
              onSave={() => void save()}
            />
          </>
        }
      >
        <div className="lg:max-w-[760px]">
          <p className="text-ink-faint mb-3 text-[11px] tracking-[0.18em] uppercase lg:mb-3.5 lg:text-xs">
            Wie ziet welke foto’s
          </p>
          <VisibilityChoice value={visibility} onChange={setVisibility} />

          <p className="text-ink-faint mt-6.5 mb-3 text-[11px] tracking-[0.18em] uppercase lg:mt-8 lg:mb-3.5 lg:text-xs">
            Kleur van het event
          </p>
          <AccentChoice value={accent} onChange={setAccent} />

          <p className="text-ink-faint mt-8 mb-3 text-[11px] tracking-[0.18em] uppercase lg:mb-3.5 lg:text-xs">
            Toegang
          </p>
          <PrivateGalleryChoice value={isPrivate} onChange={setIsPrivate} />

          {/* Buiten de opslaanbalk: uitnodigen is meteen raak en heeft geen
              "opslaan" nodig — er gaat een mail de deur uit op het moment dat
              je op de knop drukt. Zou het meelopen met de balk, dan zou je een
              uitnodiging kunnen "annuleren" die al verstuurd is. */}
          <p className="text-ink-faint mt-8 mb-3 text-[11px] tracking-[0.18em] uppercase lg:mb-3.5 lg:text-xs">
            Mensen
          </p>
          <EventPeople eventId={subject.id} />
        </div>
      </EventScreen>
    </>
  )
}
